/**
 * Straight-line snippets on purpose: no helper function, no route, no framework setup. The reader
 * is here to learn the four steps -- parse the header, recompute the HMAC, compare, check the age
 * -- and wrapping them in an app makes that harder to see, not easier.
 *
 * The live secret is never substituted in. It is served only by the audited reveal action, and a
 * copyable snippet would put it on the page with no record of who saw it.
 */

const NODE_EXAMPLE = `const crypto = require('crypto')

const secret = process.env.CRM_WEBHOOK_SECRET
const raw = req.body // raw bytes: express.raw({ type: 'application/json' })

const { t, v1 = '' } = Object.fromEntries(
  (req.get('X-CRM-Signature') ?? '').split(',').map((part) => part.split('='))
)

const expected = crypto
  .createHmac('sha256', secret)
  .update(\`\${t}.\`) // the signed value is "<t>.<raw body>"
  .update(raw)
  .digest('hex')

const isValid =
  v1.length === expected.length &&
  crypto.timingSafeEqual(Buffer.from(v1), Buffer.from(expected)) &&
  Math.abs(Date.now() / 1000 - Number(t)) < 300 // an old t is a replay, not a slow delivery`

const PYTHON_EXAMPLE = `import hashlib
import hmac
import os
import time

secret = os.environ["CRM_WEBHOOK_SECRET"]
raw = request.body  # Django. FastAPI: await request.body(). Flask: request.get_data()

parts = dict(
    p.split("=", 1)
    for p in request.headers.get("X-CRM-Signature", "").split(",")
    if "=" in p
)
t, v1 = parts.get("t", ""), parts.get("v1", "")

# The signed value is "<t>.<raw body>"
expected = hmac.new(secret.encode(), f"{t}.".encode() + raw, hashlib.sha256).hexdigest()

is_valid = (
    hmac.compare_digest(v1, expected)
    and t.isdigit()
    and abs(time.time() - int(t)) < 300  # an old t is a replay, not a slow delivery
)`

/** Community template for GitHub's scheme -- the closest starting point, not a drop-in. */
export const N8N_TEMPLATE_URL =
  'https://n8n.io/workflows/8906-secure-github-webhooks-with-hmac256-signature-validation/'

export const SIGNATURE_LANGUAGES = ['Node.js', 'Python'] as const

export type SignatureLanguage = (typeof SIGNATURE_LANGUAGES)[number]

export const SIGNATURE_EXAMPLES: Record<SignatureLanguage, string> = {
  'Node.js': NODE_EXAMPLE,
  Python: PYTHON_EXAMPLE
}
